import { StrictMode } from 'react';

import log from 'loglevel';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { ToastProvider } from '@admin-platform/ui/components';

import App from './App';
import AuthorizedApolloProvider from './components/AuthorizedApolloProvider/AuthorizedApolloProvider';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt/InstallPrompt';
import LocalAuthProvider from './components/LocalAuthProvider/LocalAuthProvider';
import OfflineBanner from './components/OfflineBanner/OfflineBanner';
import PwaUpdatePrompt from './components/PwaUpdatePrompt/PwaUpdatePrompt';
import { SetupCheckWrapper } from './components/SetupCheckWrapper/SetupCheckWrapper';
import { TenantErrorBoundary } from './components/TenantErrorBoundary/TenantErrorBoundary';
import { TenantProvider } from './contexts/TenantContext';
import { initSentry } from './sentry';

initSentry();

log.info(`🔐 Using authentication provider: local`);

import '@admin-platform/ui/theme/styles.css';
import './css/debug.css';
import './css/index.css';
import './css/preflight.css';

// Import auth recovery utilities (makes them available in console)
import './utils/auth-recovery';

// Configure logging based on environment
const logLevel = import.meta.env.VITE_LOG_LEVEL || (import.meta.env.DEV ? 'info' : 'error');
log.setLevel(log.levels[logLevel.toUpperCase() as keyof typeof log.levels] || log.levels.ERROR);
console.info(`🔧 Logging enabled at ${logLevel.toUpperCase()} level`);

try {
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
      <BrowserRouter>
        {/* Check if system is set up before showing auth */}
        <SetupCheckWrapper>
          <LocalAuthProvider>
            <AuthorizedApolloProvider>
              <TenantProvider>
                <ToastProvider>
                  {/* @ts-expect-error - React 18 + TypeScript strict mode class component issue */}
                  <TenantErrorBoundary>
                    {/* @ts-expect-error - React 18 + TypeScript strict mode class component issue */}
                    <ErrorBoundary>
                      <App />
                    </ErrorBoundary>
                  </TenantErrorBoundary>
                  {/* PWA "new version available" prompt (service-worker driven) */}
                  <PwaUpdatePrompt />
                  {/* PWA offline indicator */}
                  <OfflineBanner />
                  {/* Mobile "add to home screen" hint (auto once/24h, until installed) */}
                  <InstallPrompt />
                </ToastProvider>
              </TenantProvider>
            </AuthorizedApolloProvider>
          </LocalAuthProvider>
        </SetupCheckWrapper>
      </BrowserRouter>
    </StrictMode>,
  );
} catch (error) {
  log.error('Error rendering full React app:', error);
  // Fallback error display
  document.body.innerHTML = `
    <div style="padding: 2rem; background: #ffe6e6; color: #d00; font-family: Arial, sans-serif;">
      <h1>❌ Application Error</h1>
      <p>There was an error loading the application:</p>
      <pre style="background: #f5f5f5; padding: 1rem; border-radius: 4px; overflow: auto; white-space: pre-wrap;">${error}</pre>
      <p>Please check the browser console for more details.</p>
    </div>
  `;
}
